import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { auth, ApiError } from '../lib/api'
import { AuthLayout, ErrorBox, NoticeBox, Field, SubmitButton } from '../components/AuthLayout'

export function Verify() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [params] = useSearchParams()
  const userId = Number(params.get('user_id') || 0)
  const mailConfigured = params.get('mail_configured') === 'true'
  // Absent means an older link; treat that as sent so this only speaks up when it knows.
  const mailSent = params.get('mail_sent') !== 'false'
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      await auth.verify(userId, code)
      // Same reasoning as Login/TotpLogin/TotpSetupConfirm: verify() just planted a
      // session cookie, but a `['me']` query fetched earlier in this SPA session (e.g.
      // an unauthenticated visit to /dashboard before signing up) is still within its
      // staleTime and would otherwise be served straight from cache — a cached 401 that
      // bounces the freshly-verified user right back to /login.
      await queryClient.invalidateQueries({ queryKey: ['me'] })
      navigate('/dashboard')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Code klopt niet of is verlopen.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthLayout title="Bevestig je e-mail">
      <ErrorBox>{error}</ErrorBox>
      {!mailConfigured && (
        <NoticeBox>
          We zitten nog in een besloten testfase: verificatiecodes worden nog niet automatisch gemaild. Neem contact
          op met hallo@mentiontrend.com om je code te ontvangen.
        </NoticeBox>
      )}
      {/* Configured but rejected. Without this the page says "check your inbox" for a mail
          that was never accepted, and the visitor waits for something that is not coming. */}
      {mailConfigured && !mailSent && (
        <NoticeBox>
          Het versturen van je code is niet gelukt. Neem contact op met hallo@mentiontrend.com,
          dan helpen we je verder.
        </NoticeBox>
      )}
      <p className="mb-4 text-sm text-ink-soft">We hebben een code van zes cijfers gestuurd.</p>
      <form onSubmit={onSubmit}>
        <Field
          label="Code"
          type="text"
          inputMode="numeric"
          pattern="[0-9]{6}"
          maxLength={6}
          required
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <SubmitButton disabled={busy}>Bevestigen</SubmitButton>
      </form>
    </AuthLayout>
  )
}
